import { LockingScript, P2PKH, PrivateKey, Script, Transaction, fromUtxo } from '@treechat/bsv-sdk'
import OrdLock, {
    ORD_LOCK_PAYOUT_LAYOUT_V2,
    ORD_LOCK_V2_OUTPUT_INDEXES,
} from '../../../bsv/vendor/js-1sat-ord/src/templates/ordLock'
import type {
    OrdinalMarketApprovalRole,
    OrdinalMarketIntent,
    OrdinalMarketIntentStorage,
    OrdinalMarketOperation,
    OrdinalMarketPublicIdentity,
    OrdinalMarketTreechatFee,
} from './ordinal-market-intent'
import { saveOrdinalMarketIntent } from './ordinal-market-intent'
import {
    type OrdinalPrevoutScript,
    discardOrdinalPrevoutScript,
    ordinalPrevoutScriptFromHex,
    signOrdinalPrevoutInput,
} from './ordinal-prevout-signing'

const MAX_SCRIPT_BYTES = 8 * 1024 * 1024
const MAX_PARENT_TRANSACTION_BYTES = 20 * 1024 * 1024
const HEX_32_BYTES = /^[0-9a-f]{64}$/i
const OUTPOINT = /^([0-9a-f]{64})_([0-9]+)$/i

export type OrdinalMarketInputRole =
    | 'seller_ordinal'
    | 'seller_payment'
    | 'ordlock'
    | 'buyer_payment'

export type OrdinalMarketInputSigner = 'seller' | 'buyer' | 'contract'

export type OrdinalMarketOutputRole =
    | 'ordlock'
    | 'ordinal'
    | 'seller_proceeds'
    | 'treechat_fee'
    | 'buyer_change'
    | 'seller_change'

export type OrdinalMarketInput = {
    txid: string
    vout: number
    satoshis: number
    scriptPubKey?: string
    scriptSha256: string
    sequence: number
    role: OrdinalMarketInputRole
    signer: OrdinalMarketInputSigner
}

export type OrdinalMarketOutput = {
    satoshis: number
    script: string
    role: OrdinalMarketOutputRole
}

/** Stable transaction terms. An offer uses this exact object for both approvals. */
export type OrdinalMarketManifest = {
    version: 1
    operation: OrdinalMarketOperation
    authorizationId: string
    orderId: string
    layoutVersion: 2
    sourceOutpoint: string
    parentTxid?: string
    priceSats: number
    sellerProceedsSats: number
    treechatFee: OrdinalMarketTreechatFee
    networkFeeSatoshis: number
    seller: OrdinalMarketPublicIdentity
    buyer?: OrdinalMarketPublicIdentity
    txVersion: number
    lockTime: number
    inputs: OrdinalMarketInput[]
    outputs: OrdinalMarketOutput[]
}

/** Request-specific identity and the exact subset this device is allowed to sign. */
export type OrdinalMarketApproval = {
    signingRequestId: string
    attemptId: string
    userId: string
    deviceId: string
    walletGeneration: string
    approvalRole: OrdinalMarketApprovalRole
    assignedInputIndexes: number[]
}

export type OrdinalMarketSigningRequest = {
    manifest: OrdinalMarketManifest
    approval: OrdinalMarketApproval
    intent: OrdinalMarketIntent
    expectedUserId: string
    expectedDeviceId: string
    /** Streamed separately when this approval signs a large inscription script. */
    ordinalPrevoutScript?: OrdinalPrevoutScript
    /** Hydrated from immutable server artifacts for a chained listing purchase; never persisted. */
    parentTransactionHex?: string
    payPk: PrivateKey | string
    ordPk: PrivateKey | string
}

export type OrdinalMarketSignature = {
    /** SHA-256 of canonical JSON `{ manifest, approval }`. */
    manifestDigest: string
    inputIndex: number
    txid: string
    vout: number
    unlockingScriptHex: string
}

function canonicalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
                .map(([key, entry]) => [key, canonicalize(entry)]),
        )
    }
    return value
}

function bytesToHex(bytes: ArrayBuffer): string {
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256Hex(value: string | Uint8Array): Promise<string> {
    if (!globalThis.crypto?.subtle) throw new Error('WebCrypto SHA-256 is unavailable')
    const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value
    return bytesToHex(await globalThis.crypto.subtle.digest('SHA-256', bytes))
}

/** The stable transaction digest, useful for proving Accept reused the stored offer. */
export async function ordinalMarketTransactionDigest(
    manifest: OrdinalMarketManifest,
): Promise<string> {
    return sha256Hex(JSON.stringify(canonicalize(manifest)))
}

/** The submission digest binds stable transaction bytes to this device approval. */
export async function ordinalMarketApprovalDigest(
    manifest: OrdinalMarketManifest,
    approval: OrdinalMarketApproval,
): Promise<string> {
    return sha256Hex(JSON.stringify(canonicalize({ manifest, approval })))
}

/** Persists the exact public approval terms needed to resume after a WebView remount. */
export async function persistOrdinalMarketSigningIntent(
    manifest: OrdinalMarketManifest,
    approval: OrdinalMarketApproval,
    maxNetworkFeeSatoshis: number,
    options: {
        storage?: OrdinalMarketIntentStorage
        now?: number
        ttlMs?: number
    } = {},
): Promise<OrdinalMarketIntent> {
    const changeIndex =
        manifest.operation === 'ordinal_market_listing_parent'
            ? 1
            : ORD_LOCK_V2_OUTPUT_INDEXES.trailing
    const changeOutput = manifest.outputs[changeIndex]
    const changeRole =
        manifest.operation === 'ordinal_market_listing_parent' ? 'seller_change' : 'buyer_change'
    const changeAddress =
        changeRole === 'seller_change'
            ? manifest.seller.paymentAddress
            : manifest.buyer?.paymentAddress
    if (changeOutput && (changeOutput.role !== changeRole || !changeAddress)) {
        throw new Error('market manifest change cannot be persisted safely')
    }
    return saveOrdinalMarketIntent(
        {
            version: 1,
            operation: manifest.operation,
            authorizationId: manifest.authorizationId,
            orderId: manifest.orderId,
            signingRequestId: approval.signingRequestId,
            attemptId: approval.attemptId,
            manifestDigest: await ordinalMarketApprovalDigest(manifest, approval),
            approvalRole: approval.approvalRole,
            assignedInputIndexes: [...approval.assignedInputIndexes],
            userId: approval.userId,
            deviceId: approval.deviceId,
            walletGeneration: approval.walletGeneration,
            sourceOutpoint: manifest.sourceOutpoint,
            ...(manifest.parentTxid ? { parentTxid: manifest.parentTxid } : {}),
            priceSats: manifest.priceSats,
            sellerProceedsSats: manifest.sellerProceedsSats,
            treechatFee: { ...manifest.treechatFee },
            layoutVersion: manifest.layoutVersion,
            seller: { ...manifest.seller },
            ...(manifest.buyer ? { buyer: { ...manifest.buyer } } : {}),
            expectedChange:
                changeOutput && changeAddress
                    ? {
                          role: changeRole,
                          address: changeAddress,
                          satoshis: changeOutput.satoshis,
                      }
                    : null,
            maxNetworkFeeSatoshis,
        },
        options,
    )
}

function normalizedHex(value: string): string {
    return value.toLowerCase()
}

function requireText(name: string, value: string): void {
    if (!value) throw new Error(`${name} is required`)
}

function requireInteger(name: string, value: number, minimum = 0): void {
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new Error(`${name} must be a safe integer >= ${minimum}`)
    }
}

function requireTxid(name: string, value: string): void {
    if (!HEX_32_BYTES.test(value)) throw new Error(`${name} must be a 32-byte transaction id`)
}

function requireHex(name: string, value: string): void {
    if (!value || value.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(value)) {
        throw new Error(`${name} must be hexadecimal`)
    }
}

function scriptFromHex(name: string, hex: string): LockingScript {
    requireHex(name, hex)
    if (hex.length / 2 > MAX_SCRIPT_BYTES) {
        throw new Error(`${name} exceeds ${MAX_SCRIPT_BYTES} bytes`)
    }
    return LockingScript.fromHex(hex)
}

function expectedP2PKHScript(address: string): string {
    return new P2PKH().lock(address).toHex()
}

function assertP2PKH(script: Script, address: string, name: string): void {
    if (normalizedHex(script.toHex()) !== normalizedHex(expectedP2PKHScript(address))) {
        throw new Error(`${name} must use the expected P2PKH locking script`)
    }
}

function keyFor(value: PrivateKey | string | undefined, name: string): PrivateKey {
    if (!value) throw new Error(`${name} is required for this approval`)
    return typeof value === 'string' ? PrivateKey.fromWif(value) : value
}

function assertKeyAddress(name: string, key: PrivateKey, expectedAddress: string): void {
    if (key.toAddress() !== expectedAddress) {
        throw new Error(`${name} does not derive ${expectedAddress}`)
    }
}

async function assertScriptHash(
    name: string,
    scriptHex: string,
    expectedHash: string,
): Promise<void> {
    if (!HEX_32_BYTES.test(expectedHash)) {
        throw new Error(`${name} scriptSha256 must be a SHA-256 digest`)
    }
    const bytes = Uint8Array.from(scriptHex.match(/../g)!.map(byte => Number.parseInt(byte, 16)))
    if ((await sha256Hex(bytes)) !== normalizedHex(expectedHash)) {
        throw new Error(`${name} script hash does not match the manifest`)
    }
}

function identityEquals(
    left: OrdinalMarketPublicIdentity | undefined,
    right: OrdinalMarketPublicIdentity | undefined,
): boolean {
    if (!left || !right) return left === right
    return (
        left.userId === right.userId &&
        left.paymentAddress === right.paymentAddress &&
        left.ordinalsAddress === right.ordinalsAddress &&
        normalizedHex(left.walletGeneration) === normalizedHex(right.walletGeneration)
    )
}

async function assertIdentity(name: string, identity: OrdinalMarketPublicIdentity): Promise<void> {
    requireText(`${name}.userId`, identity.userId)
    requireText(`${name}.paymentAddress`, identity.paymentAddress)
    requireText(`${name}.ordinalsAddress`, identity.ordinalsAddress)
    const expectedGeneration = await sha256Hex(
        `${identity.paymentAddress}:${identity.ordinalsAddress}`,
    )
    if (normalizedHex(identity.walletGeneration) !== expectedGeneration) {
        throw new Error(`${name}.walletGeneration does not match its wallet addresses`)
    }
}

function parseOutpoint(sourceOutpoint: string): { txid: string; vout: number } {
    const match = sourceOutpoint.match(OUTPOINT)
    if (!match) throw new Error('sourceOutpoint must use txid_vout format')
    const vout = Number(match[2])
    requireInteger('sourceOutpoint vout', vout)
    return { txid: match[1].toLowerCase(), vout }
}

/** Reconstructs the exact v2 listing script independently of the prepared transaction. */
export function buildFeeAwareOrdLockScript(
    manifest: Pick<
        OrdinalMarketManifest,
        'layoutVersion' | 'seller' | 'sellerProceedsSats' | 'treechatFee'
    >,
): Script {
    if (manifest.layoutVersion !== ORD_LOCK_PAYOUT_LAYOUT_V2) {
        throw new Error('ordinal market payout layout must be v2')
    }
    return new OrdLock().lockV2(
        manifest.seller.ordinalsAddress,
        {
            satoshis: manifest.sellerProceedsSats,
            lockingScript: new P2PKH().lock(manifest.seller.paymentAddress),
        },
        {
            satoshis: manifest.treechatFee.satoshis,
            lockingScript: new P2PKH().lock(manifest.treechatFee.address),
        },
    )
}

function expectedApproval(
    operation: OrdinalMarketOperation,
    role: OrdinalMarketApprovalRole,
): boolean {
    return (
        (operation === 'ordinal_market_listing_parent' && role === 'seller_listing') ||
        (operation === 'ordinal_market_listing_purchase' && role === 'buyer_purchase') ||
        (operation === 'ordinal_market_offer' &&
            (role === 'buyer_offer' || role === 'seller_accept'))
    )
}

function expectedAssignedIndexes(
    manifest: OrdinalMarketManifest,
    approvalRole: OrdinalMarketApprovalRole,
): number[] {
    if (approvalRole === 'seller_listing') return manifest.inputs.map((_, index) => index)
    if (approvalRole === 'seller_accept') return [0]
    return manifest.inputs.slice(1).map((_, index) => index + 1)
}

function arraysEqual(left: number[], right: number[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index])
}

function actorForApproval(
    manifest: OrdinalMarketManifest,
    role: OrdinalMarketApprovalRole,
): OrdinalMarketPublicIdentity {
    if (role === 'seller_listing' || role === 'seller_accept') return manifest.seller
    if (!manifest.buyer) throw new Error('buyer identity is required for buyer approval')
    return manifest.buyer
}

function assertIntentTerms(
    manifest: OrdinalMarketManifest,
    approval: OrdinalMarketApproval,
    intent: OrdinalMarketIntent,
    digest: string,
): void {
    if (intent.expiresAt <= Date.now()) throw new Error('ordinal market intent has expired')
    if (
        intent.operation !== manifest.operation ||
        intent.authorizationId !== manifest.authorizationId ||
        intent.orderId !== manifest.orderId ||
        intent.signingRequestId !== approval.signingRequestId ||
        intent.attemptId !== approval.attemptId ||
        intent.approvalRole !== approval.approvalRole ||
        intent.userId !== approval.userId ||
        intent.deviceId !== approval.deviceId ||
        normalizedHex(intent.walletGeneration) !== normalizedHex(approval.walletGeneration) ||
        normalizedHex(intent.manifestDigest) !== digest ||
        intent.sourceOutpoint.toLowerCase() !== manifest.sourceOutpoint.toLowerCase() ||
        (intent.parentTxid || '').toLowerCase() !== (manifest.parentTxid || '').toLowerCase() ||
        intent.priceSats !== manifest.priceSats ||
        intent.sellerProceedsSats !== manifest.sellerProceedsSats ||
        intent.treechatFee.address !== manifest.treechatFee.address ||
        intent.treechatFee.satoshis !== manifest.treechatFee.satoshis ||
        intent.layoutVersion !== manifest.layoutVersion ||
        !identityEquals(intent.seller, manifest.seller) ||
        !identityEquals(intent.buyer, manifest.buyer) ||
        !arraysEqual(intent.assignedInputIndexes, approval.assignedInputIndexes)
    ) {
        throw new Error('ordinal market intent does not match the signing request')
    }
}

function assertChange(
    manifest: OrdinalMarketManifest,
    intent: OrdinalMarketIntent,
    output: OrdinalMarketOutput | undefined,
    role: 'buyer_change' | 'seller_change',
    address: string,
): void {
    if (!intent.expectedChange) {
        if (output) throw new Error('transaction contains unexpected change')
        return
    }
    if (
        intent.expectedChange.role !== role ||
        intent.expectedChange.address !== address ||
        !output ||
        output.role !== role ||
        output.satoshis !== intent.expectedChange.satoshis ||
        normalizedHex(output.script) !== normalizedHex(expectedP2PKHScript(address))
    ) {
        throw new Error('transaction change does not match the local intent')
    }
}

function assertOutputs(manifest: OrdinalMarketManifest, intent: OrdinalMarketIntent): void {
    const sellerScript = expectedP2PKHScript(manifest.seller.paymentAddress)
    const feeScript = expectedP2PKHScript(manifest.treechatFee.address)
    if (manifest.operation === 'ordinal_market_listing_parent') {
        if (manifest.outputs.length < 1 || manifest.outputs.length > 2) {
            throw new Error('listing parent must contain only OrdLock and optional seller change')
        }
        const ordlock = manifest.outputs[0]
        if (
            ordlock.role !== 'ordlock' ||
            ordlock.satoshis !== 1 ||
            normalizedHex(ordlock.script) !==
                normalizedHex(buildFeeAwareOrdLockScript(manifest).toHex())
        ) {
            throw new Error('listing parent output 0 does not reconstruct the fee-aware OrdLock')
        }
        assertChange(
            manifest,
            intent,
            manifest.outputs[1],
            'seller_change',
            manifest.seller.paymentAddress,
        )
        return
    }

    if (!manifest.buyer) throw new Error('buyer identity is required')
    if (
        manifest.outputs.length < ORD_LOCK_V2_OUTPUT_INDEXES.trailing ||
        manifest.outputs.length > ORD_LOCK_V2_OUTPUT_INDEXES.trailing + 1
    ) {
        throw new Error('market settlement has an invalid output count')
    }
    const ordinal = manifest.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.ordinal]
    const seller = manifest.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.seller]
    const fee = manifest.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.fee]
    const change = manifest.outputs[ORD_LOCK_V2_OUTPUT_INDEXES.trailing]
    if (
        ordinal.role !== 'ordinal' ||
        ordinal.satoshis !== 1 ||
        normalizedHex(ordinal.script) !==
            normalizedHex(expectedP2PKHScript(manifest.buyer.ordinalsAddress))
    ) {
        throw new Error('output 0 must place the one-satoshi ordinal with the buyer')
    }
    if (
        seller.role !== 'seller_proceeds' ||
        seller.satoshis !== manifest.sellerProceedsSats ||
        normalizedHex(seller.script) !== normalizedHex(sellerScript)
    ) {
        throw new Error('output 1 must pay the exact seller proceeds')
    }
    if (
        fee.role !== 'treechat_fee' ||
        fee.satoshis !== manifest.treechatFee.satoshis ||
        normalizedHex(fee.script) !== normalizedHex(feeScript)
    ) {
        throw new Error('output 2 must pay the exact Treechat fee')
    }
    assertChange(manifest, intent, change, 'buyer_change', manifest.buyer.paymentAddress)
}

function assertInputLayout(manifest: OrdinalMarketManifest): void {
    if (manifest.inputs.length < 1) throw new Error('market manifest needs inputs')
    const source = parseOutpoint(manifest.sourceOutpoint)
    const first = manifest.inputs[0]
    if (first.satoshis !== 1) throw new Error('market input 0 must contain exactly one satoshi')

    if (manifest.operation === 'ordinal_market_listing_parent') {
        if (
            first.role !== 'seller_ordinal' ||
            first.signer !== 'seller' ||
            first.txid.toLowerCase() !== source.txid ||
            first.vout !== source.vout
        ) {
            throw new Error('listing parent input 0 must be the declared seller ordinal')
        }
        if (
            manifest.inputs
                .slice(1)
                .some(input => input.role !== 'seller_payment' || input.signer !== 'seller')
        ) {
            throw new Error('listing parent funding inputs must be assigned to the seller')
        }
        if (manifest.parentTxid) throw new Error('listing parent must not declare parentTxid')
        return
    }

    if (manifest.operation === 'ordinal_market_listing_purchase') {
        requireTxid('parentTxid', manifest.parentTxid || '')
        if (
            first.role !== 'ordlock' ||
            first.signer !== 'contract' ||
            first.txid.toLowerCase() !== manifest.parentTxid!.toLowerCase() ||
            first.vout !== 0
        ) {
            throw new Error('listing purchase input 0 must spend the stored OrdLock parent')
        }
    } else if (
        first.role !== 'seller_ordinal' ||
        first.signer !== 'seller' ||
        first.txid.toLowerCase() !== source.txid ||
        first.vout !== source.vout
    ) {
        throw new Error('offer input 0 must be the declared seller ordinal')
    }
    if (
        manifest.inputs.length < 2 ||
        manifest.inputs
            .slice(1)
            .some(input => input.role !== 'buyer_payment' || input.signer !== 'buyer')
    ) {
        throw new Error('settlement funding inputs must be assigned to the buyer')
    }
}

async function validateRequest(request: OrdinalMarketSigningRequest): Promise<{
    digest: string
    scripts: Array<LockingScript | undefined>
    ordinalPrevout: OrdinalPrevoutScript | undefined
}> {
    const { manifest, approval, intent } = request
    if (manifest.version !== 1) throw new Error('unsupported ordinal market manifest version')
    if (manifest.layoutVersion !== ORD_LOCK_PAYOUT_LAYOUT_V2) {
        throw new Error('ordinal market payout layout must be v2')
    }
    requireText('authorizationId', manifest.authorizationId)
    requireText('orderId', manifest.orderId)
    requireInteger('priceSats', manifest.priceSats, 1)
    requireInteger('sellerProceedsSats', manifest.sellerProceedsSats, 1)
    requireInteger('treechatFee.satoshis', manifest.treechatFee.satoshis, 1)
    requireText('treechatFee.address', manifest.treechatFee.address)
    if (manifest.sellerProceedsSats + manifest.treechatFee.satoshis !== manifest.priceSats) {
        throw new Error('seller proceeds and Treechat fee must equal the price')
    }
    requireInteger('txVersion', manifest.txVersion)
    requireInteger('lockTime', manifest.lockTime)
    requireInteger('networkFeeSatoshis', manifest.networkFeeSatoshis)
    await assertIdentity('seller', manifest.seller)
    if (manifest.buyer) await assertIdentity('buyer', manifest.buyer)
    if (manifest.operation === 'ordinal_market_listing_parent' ? manifest.buyer : !manifest.buyer) {
        throw new Error('buyer identity is required after the listing parent')
    }
    if (!expectedApproval(manifest.operation, approval.approvalRole)) {
        throw new Error('approval role does not match the market operation')
    }
    requireText('signingRequestId', approval.signingRequestId)
    requireText('attemptId', approval.attemptId)
    requireText('deviceId', approval.deviceId)
    const actor = actorForApproval(manifest, approval.approvalRole)
    if (
        approval.userId !== request.expectedUserId ||
        approval.deviceId !== request.expectedDeviceId ||
        approval.userId !== actor.userId ||
        normalizedHex(approval.walletGeneration) !== normalizedHex(actor.walletGeneration)
    ) {
        throw new Error('approval is for a different user, device, or wallet generation')
    }
    const assigned = expectedAssignedIndexes(manifest, approval.approvalRole)
    if (!arraysEqual(approval.assignedInputIndexes, assigned)) {
        throw new Error('approval assignedInputIndexes does not match the exact signer subset')
    }
    const digest = await ordinalMarketApprovalDigest(manifest, approval)
    assertIntentTerms(manifest, approval, intent, digest)

    assertInputLayout(manifest)
    assertOutputs(manifest, intent)
    if (manifest.operation === 'ordinal_market_listing_purchase') {
        requireHex('parent transaction', request.parentTransactionHex || '')
        if (request.parentTransactionHex!.length / 2 > MAX_PARENT_TRANSACTION_BYTES) {
            throw new Error(`parent transaction exceeds ${MAX_PARENT_TRANSACTION_BYTES} bytes`)
        }
        let parent: Transaction
        try {
            parent = Transaction.fromHex(request.parentTransactionHex!)
        } catch {
            throw new Error('parent transaction could not be parsed')
        }
        if (normalizedHex(parent.toHex()) !== normalizedHex(request.parentTransactionHex!)) {
            throw new Error('parent transaction must use its exact canonical serialization')
        }
        if (normalizedHex(parent.id('hex')) !== normalizedHex(manifest.parentTxid!)) {
            throw new Error('parent transaction bytes do not match parentTxid')
        }
        const source = parseOutpoint(manifest.sourceOutpoint)
        const parentOrdinalInput = parent.inputs[0]
        if (
            !parentOrdinalInput ||
            normalizedHex(parentOrdinalInput.sourceTXID || '') !== source.txid ||
            parentOrdinalInput.sourceOutputIndex !== source.vout
        ) {
            throw new Error('parent transaction does not spend the declared ordinal source')
        }
        const parentOrdLock = parent.outputs[0]
        if (
            !parentOrdLock ||
            parentOrdLock.satoshis !== 1 ||
            normalizedHex(parentOrdLock.lockingScript.toHex()) !==
                normalizedHex(buildFeeAwareOrdLockScript(manifest).toHex())
        ) {
            throw new Error('parent transaction output 0 is not the exact fee-aware OrdLock')
        }
    } else if (request.parentTransactionHex !== undefined) {
        throw new Error('parent transaction bytes are only valid for listing purchases')
    }
    const seenOutpoints = new Set<string>()
    const scripts: Array<LockingScript | undefined> = []
    let ordinalPrevout: OrdinalPrevoutScript | undefined
    for (const [index, input] of manifest.inputs.entries()) {
        requireTxid(`inputs[${index}].txid`, input.txid)
        requireInteger(`inputs[${index}].vout`, input.vout)
        requireInteger(`inputs[${index}].satoshis`, input.satoshis, 1)
        requireInteger(`inputs[${index}].sequence`, input.sequence)
        if (!HEX_32_BYTES.test(input.scriptSha256)) {
            throw new Error(`inputs[${index}].scriptSha256 must be a SHA-256 digest`)
        }
        const outpoint = `${input.txid.toLowerCase()}_${input.vout}`
        if (seenOutpoints.has(outpoint))
            throw new Error('market inputs contain a duplicate outpoint')
        seenOutpoints.add(outpoint)
        if (input.role === 'seller_ordinal') {
            // Hashed and layout-checked while streaming, when it is signed or verified.
            let prevout = request.ordinalPrevoutScript
            if (input.scriptPubKey) {
                requireHex(`input ${index} locking script`, input.scriptPubKey)
                prevout = ordinalPrevoutScriptFromHex(input.scriptPubKey)
            }
            scripts.push(undefined)
            if (!prevout) {
                if (
                    manifest.operation === 'ordinal_market_offer' &&
                    approval.approvalRole === 'buyer_offer' &&
                    index === 0 &&
                    !approval.assignedInputIndexes.includes(index)
                ) {
                    continue
                }
                throw new Error(`input ${index} is missing its locking script`)
            }
            if (approval.assignedInputIndexes.includes(index)) ordinalPrevout = prevout
            else {
                await signOrdinalPrevoutInput({
                    tx: manifest,
                    inputIndex: index,
                    satoshis: input.satoshis,
                    script: prevout,
                    expectedScriptSha256: input.scriptSha256,
                    ordinalsAddress: manifest.seller.ordinalsAddress,
                    allowPlainP2PKH: true,
                })
            }
            continue
        }
        if (!input.scriptPubKey) throw new Error(`input ${index} is missing its locking script`)
        const script = scriptFromHex(`input ${index} locking script`, input.scriptPubKey)
        await assertScriptHash(`input ${index}`, script.toHex(), input.scriptSha256)
        scripts.push(script)

        if (input.role === 'seller_payment') {
            assertP2PKH(script, manifest.seller.paymentAddress, `seller payment input ${index}`)
        } else if (input.role === 'buyer_payment') {
            assertP2PKH(script, manifest.buyer!.paymentAddress, `buyer payment input ${index}`)
        } else {
            const expectedOrdLock = buildFeeAwareOrdLockScript(manifest)
            if (normalizedHex(script.toHex()) !== normalizedHex(expectedOrdLock.toHex())) {
                throw new Error('OrdLock parent script does not match the fee-aware terms')
            }
        }
    }

    for (const [index, output] of manifest.outputs.entries()) {
        requireInteger(`outputs[${index}].satoshis`, output.satoshis, 1)
        scriptFromHex(`output ${index} script`, output.script)
    }
    const inputTotal = manifest.inputs.reduce((sum, input) => sum + input.satoshis, 0)
    const outputTotal = manifest.outputs.reduce((sum, output) => sum + output.satoshis, 0)
    if (inputTotal - outputTotal !== manifest.networkFeeSatoshis) {
        throw new Error('network fee does not equal inputs minus outputs')
    }
    requireInteger('maxNetworkFeeSatoshis', intent.maxNetworkFeeSatoshis)
    if (manifest.networkFeeSatoshis > intent.maxNetworkFeeSatoshis) {
        throw new Error('network fee exceeds the local intent cap')
    }
    const outputScriptBytes = manifest.outputs.reduce(
        (sum, output) => sum + output.script.length / 2,
        0,
    )
    const estimatedBytes =
        10 + manifest.inputs.length * 180 + manifest.outputs.length * 10 + outputScriptBytes
    const localSafetyLimit = Math.max(1000, estimatedBytes * 20)
    if (manifest.networkFeeSatoshis > localSafetyLimit) {
        throw new Error('network fee exceeds the browser safety limit')
    }
    return { digest, scripts, ordinalPrevout }
}

/** Signs only the assigned user inputs and returns no transaction or private material. */
export async function signOrdinalMarketInputs(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    try {
        return await signValidatedOrdinalMarketInputs(request)
    } finally {
        discardOrdinalPrevoutScript(request.ordinalPrevoutScript)
    }
}

async function signValidatedOrdinalMarketInputs(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    const { manifest, approval } = request
    const { digest, scripts, ordinalPrevout } = await validateRequest(request)
    const actor = actorForApproval(manifest, approval.approvalRole)
    const payPk = keyFor(request.payPk, 'payPk')
    const ordPk = keyFor(request.ordPk, 'ordPk')
    assertKeyAddress('payPk', payPk, actor.paymentAddress)
    assertKeyAddress('ordPk', ordPk, actor.ordinalsAddress)
    const assigned = new Set(approval.assignedInputIndexes)
    const tx = new Transaction(manifest.txVersion, [], [], manifest.lockTime)
    for (const output of manifest.outputs) {
        tx.addOutput({
            satoshis: output.satoshis,
            lockingScript: scriptFromHex('output script', output.script),
        })
    }
    let ordinalUnlockingScriptHex: string | undefined
    for (const [index, input] of manifest.inputs.entries()) {
        if (!assigned.has(index) || input.role === 'seller_ordinal') {
            tx.addInput({
                sourceTXID: input.txid,
                sourceOutputIndex: input.vout,
                sequence: input.sequence,
            })
            if (assigned.has(index)) {
                if (!ordinalPrevout) throw new Error(`input ${index} is missing its locking script`)
                // Signed from the streamed prevout; the SDK only signs the small scripts.
                ordinalUnlockingScriptHex = await signOrdinalPrevoutInput({
                    tx: manifest,
                    inputIndex: index,
                    satoshis: input.satoshis,
                    script: ordinalPrevout,
                    expectedScriptSha256: input.scriptSha256,
                    ordinalsAddress: manifest.seller.ordinalsAddress,
                    allowPlainP2PKH: true,
                    key: ordPk,
                })
            }
            continue
        }
        const script = scripts[index]
        if (!script) throw new Error(`input ${index} is missing its locking script`)
        const txInput = fromUtxo(
            {
                txid: input.txid,
                vout: input.vout,
                satoshis: input.satoshis,
                script: script.toHex(),
            },
            new P2PKH().unlock(payPk, 'all', false, input.satoshis, script),
        )
        txInput.sequence = input.sequence
        tx.addInput(txInput)
    }
    await tx.sign()

    return approval.assignedInputIndexes.map(inputIndex => {
        const input = manifest.inputs[inputIndex]
        const unlockingScript =
            input?.role === 'seller_ordinal'
                ? ordinalUnlockingScriptHex && Script.fromHex(ordinalUnlockingScriptHex)
                : tx.inputs[inputIndex]?.unlockingScript
        if (!input || !unlockingScript) throw new Error(`input ${inputIndex} was not signed`)
        const signatureBytes = unlockingScript.chunks[0]?.data
        if (!signatureBytes || signatureBytes[signatureBytes.length - 1] !== 0x41) {
            throw new Error('market user signature must use SIGHASH_ALL | SIGHASH_FORKID')
        }
        return {
            manifestDigest: digest,
            inputIndex,
            txid: input.txid,
            vout: input.vout,
            unlockingScriptHex: unlockingScript.toHex(),
        }
    })
}

function requireRequestKind(
    request: OrdinalMarketSigningRequest,
    operation: OrdinalMarketOperation,
    approvalRole: OrdinalMarketApprovalRole,
): void {
    if (
        request.manifest.operation !== operation ||
        request.approval.approvalRole !== approvalRole
    ) {
        throw new Error(`expected ${operation} with ${approvalRole} approval`)
    }
}

export async function signOrdinalListingParent(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    requireRequestKind(request, 'ordinal_market_listing_parent', 'seller_listing')
    return signOrdinalMarketInputs(request)
}

export async function signOrdinalListingPurchase(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    requireRequestKind(request, 'ordinal_market_listing_purchase', 'buyer_purchase')
    return signOrdinalMarketInputs(request)
}

export async function presignOrdinalOffer(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    requireRequestKind(request, 'ordinal_market_offer', 'buyer_offer')
    return signOrdinalMarketInputs(request)
}

export async function signOrdinalOfferAcceptance(
    request: OrdinalMarketSigningRequest,
): Promise<OrdinalMarketSignature[]> {
    requireRequestKind(request, 'ordinal_market_offer', 'seller_accept')
    return signOrdinalMarketInputs(request)
}
